import { NgIf, AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { Cart } from '@next/core-lib/cart';
import { AgrI18nSharedModule } from '@next/core-lib/i18n';
import { BehaviorSubject, Observable, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'app-total-price',
  templateUrl: './total-price.component.html',
  styleUrls: ['./total-price.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIf, MatProgressSpinner, AsyncPipe, AgrI18nSharedModule],
})
export class TotalPriceComponent {
  private readonly cartSubject: BehaviorSubject<Cart> = new BehaviorSubject<Cart>(null);

  private readonly prices: Observable<number[]> = this.cartSubject.pipe(map((cart) => this.extractPricesFromCart(cart)));

  protected readonly hasCalculatedAllPrices$: Observable<boolean> = this.cartSubject.pipe(switchMap((cart) => this.checkIfAllPricesAreCalculated(cart)));

  protected readonly totalPrice$: Observable<number> = this.prices.pipe(map((prices) => this.calculateTotalPrice(prices)));

  protected readonly currency$: Observable<string> = this.cartSubject.pipe(map((cart) => cart.lines[0]?.priceResults?.[0]?.currency || ' '));

  protected readonly showPriceSpinner$: Observable<boolean> = this.hasCalculatedAllPrices$.pipe(
    map((hasCalculatedAllPrices: boolean) => !hasCalculatedAllPrices),
  );

  @Input({ required: true })
  public set cart(v: Cart) {
    this.cartSubject.next(v);
  }

  private extractPricesFromCart = (cart: Cart): number[] =>
    cart.lines.filter((line) => line.priceResults?.[0]?.netPrice).map((line) => Number(line.priceResults[0].netPrice));

  private calculateTotalPrice = (prices: number[]): number => prices.reduce((acc, price) => acc + price, 0);

  private checkIfAllPricesAreCalculated = (cart: Cart): Observable<boolean> => this.prices.pipe(map((prices) => cart.lines.length === prices.length));
}
